import React, { FC } from 'react';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import Button from '@salesforce/design-system-react/components/button';
import { BrowserRouter as Router, RouteComponentProps, withRouter } from 'react-router-dom';
import AgencyTransaction from '../../../Shared/AgencyTransaction';
import { clearSortFromStorage } from '../../../../util/listSortStorage';
import { EVENTS_LIST_SORT_KEY, EXPENSE_INVENTORY_SCREEN_TYPE } from '../../../../util/constants';
import { ACTIVE_TYPE_LABEL_BY_STATUS } from '../../../../util/expenseInventory';

const RECORD_TYPE_EXPENSE_INVENTORY = 'Expense Inventory';
const CASE_STATUS_NEW = 'New';
const CASE_STATUS_REQUESTED = 'Requested';
const CASE_STATUS_FAILED = 'Failed';

interface EventId {
  externalId: string;
  recordID: string;
  caseNumber: string;
  orderId: string;
  status: string;
  recordType: string;
  cycleCountExternalId: string;
  isAgencyRecord: boolean;
}

interface Persona {
  personas: string;
}
// interface for props that the data table cell is getting default from data table.
export interface Props extends RouteComponentProps {
  children?: FC;
  item?: EventId;
  type?: string;
  userInfo?: Persona;
}

const OrderRequestIdCell: FC<Props> = ({
  item = {
    externalId: null,
    recordID: null,
    caseNumber: null,
    orderId: null,
    status: null,
    recordType: null,
    cycleCountExternalId: null,
    isAgencyRecord: false,
  },
  history,
}) => {
  const goToDetailsPage = (): void => {
    const {
      status,
      recordID,
      caseNumber,
      externalId,
      orderId,
      recordType,
      cycleCountExternalId,
    } = item;
    const type = status;
    // Clear Events sort when navigating to any non-Events record.
    const isEventsRecord = recordType === 'Surgery' || recordType === 'Demo' || recordType === 'Trial';
    if (!isEventsRecord) {
      clearSortFromStorage(EVENTS_LIST_SORT_KEY);
    }
    if (recordType === 'Surgery' || recordType === 'Demo' || recordType === 'Trial') {
      // If data comes for event
      history.push('/surgicalDetails', {
        eventType: recordType,
        externalId,
        id: recordID,
      });
    } else if (recordType === 'Inventory Request') {
      // If IR
      if (type === 'Favorites') {
        history.push('/inventoryFavoriteDetails', {
          inventoryRequestFav: {
            eventId: orderId,
            caseNumber,
            caseExternalId: externalId,
            orderId,
          },
        });
      } else if (
        type === 'New' ||
        type === 'Requested' ||
        (type === 'Cancelled' && !orderId && externalId) ||
        (type === 'Approved' && !orderId && externalId)
      ) {
        history.push('/inventoryRequestDetails', {
          isEventInventoryFlow: false,
          externalId,
          type: 'Open',
        });
      } else if (type === 'Failed') {
        history.push('/inventoryRequestDetails', {
          isEventInventoryFlow: false,
          externalId: orderId,
          type,
        });
      } else if (type === '3PL Review') {
        history.push('/inventoryRequestDetails', {
          isEventInventoryFlow: false,
          externalId,
          type,
        });
      } else {
        history.push('/inventoryRequestDetails', {
          isEventInventoryFlow: false,
          externalId: orderId,
          type: 'Processed',
        });
      }
    } else if (
      recordType === 'Transfer' ||
      recordType === 'Customer Transfer' ||
      recordType === 'Rep to Customer' ||
      recordType === 'Customer to Rep' ||
      recordType === 'Rep to Rep' ||
      recordType === 'Event to Event'
    ) {
      // If Inventory transfer
      let newType;
      if (status === 'New') {
        newType = 'Sent';
      } else if (status === 'Requested') {
        newType = 'Received';
      } else if (status === 'Failed') {
        newType = 'Failed';
      } else {
        newType = 'Completed';
      }
      history.push('/inventoryTransferDetails', {
        externalId,
        transferType: newType,
        visualIndicator: status,
        status,
      });
    } else if (recordType === 'Inventory Return' || recordType === 'Pickup Return') {
      // If Inventory Return
      let newType;
      if (status === 'New') {
        newType = 'Sent';
      } else if (status === 'Requested') {
        newType = 'Received';
      } else if (status === 'Failed') {
        newType = 'Failed';
      } else {
        newType = 'Completed';
      }
      history.push('/inventoryReturnDetails', {
        externalId,
        type: newType,
        orderId,
      });
    } else if (recordType === 'Cycle Count') {
      // For Cycle Count
      if (cycleCountExternalId) {
        history.push('/cycleCountDetails', {
          cycleCountExternalId,
        });
      }
    } else if (recordType === 'Bill Only' || recordType === 'Ship & Bill') {
      // for Orders
      let newType;
      if (status === 'New' || status === 'Requested' || status === 'Cancelled') {
        newType = 'Open';
      } else if (status === 'Failed') {
        newType = 'Failed';
      } else if (
        status === 'Submitted' ||
        status === 'Entered' ||
        status === 'Processed' ||
        status === 'Partial' ||
        status === 'Booked' ||
        status === 'Closed' ||
        status === 'Completed'
      ) {
        newType = 'Processed';
      } else {
        newType = 'Completed';
      }
      if (newType && orderId && newType !== 'Review' && status !== 'Cancelled') {
        history.push('/OrdersUsageDetails', { orderId, type: newType });
      } else if (newType && externalId && status === 'Cancelled' && newType !== 'Review') {
        history.push('/OrdersUsageDetails', { externalId, type: newType });
      } else {
        history.push('/OrdersUsageDetails', { externalId, type: newType });
      }
    } else if (recordType === RECORD_TYPE_EXPENSE_INVENTORY) {
      // For Expense Inventory
      let bucket;
      if (status === CASE_STATUS_NEW || status === CASE_STATUS_REQUESTED) {
        bucket = EXPENSE_INVENTORY_SCREEN_TYPE.OPEN;
      } else if (status === CASE_STATUS_FAILED) {
        bucket = EXPENSE_INVENTORY_SCREEN_TYPE.FAILED;
      } else {
        bucket = EXPENSE_INVENTORY_SCREEN_TYPE.PROCESSED;
      }
      history.push('/inventoryExpenseDetails', {
        externalId,
        caseNumber,
        type: ACTIVE_TYPE_LABEL_BY_STATUS[bucket],
      });
    }
  };

  return (
    <Router>
      <DataTableCell title={item?.recordID || ''}>
        <AgencyTransaction isAgencyRecord={item.isAgencyRecord} />
        <Button
          label={item?.recordID || item?.caseNumber || 'TBD'}
          variant="base"
          onClick={goToDetailsPage}
        />
      </DataTableCell>
    </Router>
  );
};

export default withRouter(OrderRequestIdCell);
